// type-only 导入避免与 SecondaryOnlineDetailTable（反向依赖 ../utils）形成运行时循环依赖
import type { SecondaryOnlineDetailItem } from './SecondaryOnlineDetailTable';
// type-only 导入（DurationValue.unit 窄化，避免与 SolutionTable 行类型不符）
import type { DurationValue } from './DurationInput';
// type-only 导入避免与 NegotiationTable/SolutionTable（反向依赖 ../utils）形成运行时循环依赖
import type { SolutionTableRow } from './NegotiationTable/SolutionTable';
// type-only 导入避免与 PaymentPlanTable（共享行构造器 buildPaymentPlanTableRows 返回值类型）形成运行时循环依赖
import type { PaymentPlanRow } from './PaymentPlanTable';
import { v4 as uuidv4 } from 'uuid';

import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import CryptoJS from 'crypto-js';

/** OA定标审批及之后（IP合作总金额 card / Offer确认金额分配展示范围；OFFER_CONFIRM 已删除，改用 OA_AWARD_APPROVAL/IN_PROGRESS） */
export const L2_AWARD_AND_AFTER_STATES = ['OA_AWARD_APPROVAL', 'IN_PROGRESS'];

/** 默认原币币种 / 汇率目标币种：人民币（对齐 currencyEnum 小写键，见 #utils/number；后端 CurrencyEnum 用大写 RMB） */
export const DEFAULT_CURRENCY = 'rmb';
/** 人民币等价旧代码（历史数据可能为 CNY 大写，CurrencyEnum 无 CNY） */
const CNY_CURRENCY = 'cny';
/** 同币种汇率恒为 1 */
const SAME_CURRENCY_RATE = 1;

/** 币种是否为人民币（rmb/cny，大小写不敏感；历史数据可能为 CNY 大写，CurrencyEnum 无 CNY） */
export function isRmbCurrency(currency?: string): boolean {
  if (!currency) return false;
  const c = currency.toLowerCase();
  return c === DEFAULT_CURRENCY || c === CNY_CURRENCY;
}

// 产品行字段对齐 v2RubyMigrateProductsByIdEdit 返回 ProductEditShowVo（见 services/api）
import type {
  ExchangeRateDto,
  IpCooperationDemandDetailVoL2Info,
  IpCooperationNegotiationItemVo,
  IpCooperationNewIpEnum,
  NameValueVoString,
  PaymentPlanItem,
  ProductManagerVo,
} from '#services/api';
/** 授权时长最小/最大月份：3~36 个月，每月一个选项 */
const MIN_AUTH_MONTHS = 3;
const MAX_AUTH_MONTHS = 36;
// 授权时长（3~36 个月，每月一个）：后端 IpLicenseDurationEnum 未进 swagger，走 commonoption
// 接口（ip_cooperation_license_duration）动态下发，此处保留前端静态占位（value 对齐 AuthInfo.license_duration）
export const AUTH_DURATION_OPTIONS = Array.from(
  { length: MAX_AUTH_MONTHS - MIN_AUTH_MONTHS + 1 },
  (_, i) => {
    const months = MIN_AUTH_MONTHS + i;
    return { name: `${months}个月`, value: `M${months}` };
  }
);

// 时长单位（周/月）：value 对齐后端 commonoption 接口（ip_cooperation_duration_unit）大写契约 WEEK/MONTH
export const DURATION_UNIT_OPTIONS = [
  { name: '周', value: 'WEEK' },
  { name: '月', value: 'MONTH' },
];

/**
 * 构造「IP合作游戏方详情」谈判表格行（solution_table 字段值）。
 * 从 l2_info.auth_info.online_links 映射行数据（对齐内包 flattenL2DetailForForm solution_table 构造，
 * 避免外包/内包两处映射逻辑漂移）；行携带 item_key='solution_detail' + SOLUTION 条款回复/意见
 * （ActionCell/OpinionCell 优先读行字段回显版权方操作）。
 * 外包详情 formData 复用本函数；内包仍走 flattenL2DetailForForm（未迁移，行为不变）。
 */
export function buildSolutionTableRows(
  l2?: IpCooperationDemandDetailVoL2Info | null
): SolutionTableRow[] {
  const solutionNegotiation = (l2?.negotiation_items ?? []).find(
    (i) => i.item_key === 'solution_detail'
  );
  return (l2?.auth_info?.online_links ?? []).map((item) => ({
    ...item,
    // 谈判条款标识（SOLUTION section item_key）：行携带后 SolutionTable 回复/意见列经 ActionCell/OpinionCell 回显版权方操作
    item_key: 'solution_detail',
    // 版权方回复/意见直接注入行数据（ActionCell/OpinionCell 优先读行字段，itemsByKey 仅兜底）
    action: solutionNegotiation?.action,
    opinion: solutionNegotiation?.opinion,
    duration_value: {
      duration: item.duration,
      unit: item.unit as DurationValue['unit'],
    },
    online_period:
      item.online_begin && item.online_end
        ? [dayjs(item.online_begin), dayjs(item.online_end)]
        : [],
    // 联动下线日期（镜像 online_period）：SolutionTable「联动下线日期」列 dataIndex=offline_period，
    // 需 dayjs 数组（YYYY-MM ~ YYYY-MM 渲染 + 内包编辑提交拆 offline_begin/end）；缺映射会显示 --
    offline_period:
      item.offline_begin && item.offline_end
        ? [dayjs(item.offline_begin), dayjs(item.offline_end)]
        : [],
  }));
}

/** 付款计划条款 item_key（negotiation_items 回复/意见回显 + diff 数据源；接口未 ready 前端 mock 约定，后端契约接入后对齐） */
export const PAYMENT_PLAN_ITEM_KEY = 'payment_plan';

/**
 * 付款计划（l2_info.payment_plan 嵌套 PaymentPlanItem[]）→ 表单扁平行（payment_plan）。
 * 主行（触发条件 trigger_condition）展开为付款方式子行（methods），子行继承父行节点/时间/回复/意见字段；
 * payment_node_key = trigger_condition 内容 md5（两轮同内容节点同键，作 diff 按节点分组键）；payment_type_key = 每次渲染重新生成的 uuid（仅作 ProTablePlusForm addGroup 拍平子行分组键）；
 * diff 匹配 = 节点 md5 分组 + 组内子行按 index 对齐（roundDiff.ts）。
 * methods[].payment_type 接口大写（LF/MG/ROYALTY）转小写对齐前端选项（lf/mg/royalty，BudgetOfferTable item_key）；
 * 接口 PaymentMethod 无 currency，行内 payment_currency 留空（展示币种走费用预算 currency prop）。
 * 行携带 item_key='payment_plan'：外包谈判视角回复/意见列经 ActionCell/OpinionCell 读取 editState['payment_plan']
 * （镜像 buildSolutionTableRows 注入 item_key='solution_detail'，ActionCell/OpinionCell 优先读行字段）。
 * 外包详情 formData 复用本函数；内包 flattenL2DetailForForm 亦复用（避免内/外包两处映射逻辑漂移）。
 */
export function buildPaymentPlanTableRows(
  paymentPlan?: PaymentPlanItem[],
  paymentNegotiation?: IpCooperationNegotiationItemVo
): PaymentPlanRow[] {
  const rows: PaymentPlanRow[] = [];
  (paymentPlan ?? []).forEach((item, nodeIndex) => {
    const payment_node_key = CryptoJS.MD5(
      `${JSON.stringify(item.trigger_condition)}`
    ).toString();
    const cond = item?.trigger_condition;
    // methods 空数组时补空占位子行（保证节点下至少一行，编辑态 addGroup 占位，对齐旧 payment_items 兜底逻辑）
    const methods: { payment_type?: string; amount?: number }[] = item?.methods
      ?.length
      ? item.methods
      : [{}];
    methods.forEach((method, itemIndex) => {
      rows.push({
        payment_node_key: payment_node_key,
        payment_type_key: uuidv4(),
        // 付款节点复合值（节点类型 + 时间；对齐 DurationInput 复合控件对象 value 范式，editRender custom-component 整体受控）
        // 字段 key 对齐接口 TriggerCondition（code/name/date_type/offset_value/offset_unit/fixed_date），前端不自定义重命名
        payment_node: {
          code: cond?.code,
          name: cond?.name,
          date_type: cond?.date_type,
          offset_value: cond?.offset_value,
          offset_unit: cond?.offset_unit,
          fixed_date: cond?.fixed_date,
        },
        payment_type: method.payment_type?.toLowerCase(),
        payment_amount: method.amount,
        // 谈判条款标识（payment_plan section item_key）：外包谈判视角回复/意见列经 ActionCell/OpinionCell 读 editState
        item_key: PAYMENT_PLAN_ITEM_KEY,
        action: paymentNegotiation?.action,
        opinion: paymentNegotiation?.opinion,
      });
    });
  });
  return rows;
}

/**
 * 当前展示轮次：外包端 Offer 谈判详情头部/出价方案轮次 tag。
 * 统一显示已提交轮次 current_round（产品口径：不做 +1，编辑场景也显示当前已提交轮次）。
 * 对齐后端 l2_ext.current_round 语义 = 已提交轮次数。
 */
export function resolveDisplayRound(
  _editable: boolean,
  currentRound?: number
): number {
  return currentRound ?? 0;
}

// IP×产品新/复投状态（v2IpResourceReinvestStatus 返回）→ new_ip_resource 字段映射
// （对齐 IpCooperationNewIpEnum：复投IP=0 / 新IP=1，api.ts RE_INVEST/NEW；NewIPCooperationItem 与 DetailContent 一级单 NEW 态联动共用）
const REINVEST_STATUS = 'REINVEST';
const NEW_STATUS = 'NEW';
/** new_ip_resource 字段取值：0=复投IP（对齐 IpCooperationNewIpEnum.RE_INVEST） */
const REINVEST_IP = 0;
/** new_ip_resource 字段取值：1=新IP（对齐 IpCooperationNewIpEnum.NEW） */
const NEW_IP = 1;
export function reinvestStatusToNewIpResource(
  status?: string
): IpCooperationNewIpEnum | undefined {
  if (status === REINVEST_STATUS) return REINVEST_IP;
  if (status === NEW_STATUS) return NEW_IP;
  return undefined;
}

/** 平台数据互通：0=不支持 1=支持（对齐 ProductEditShowVo.data_interconnection / 产品库固定枚举） */
export const DATA_INTERCONNECT_UNSUPPORTED = 0;
/** 平台数据互通：支持（对齐 ProductEditShowVo.data_interconnection） */
export const DATA_INTERCONNECT_SUPPORTED = 1;

export interface IPCooperationProductItem {
  /** 产品 ID（对齐 v2RubyMigrateProductsByIdEdit 返回 ProductEditShowVo.id） */
  id?: number;
  /** 产品编码（对齐 product_snapshot.identifier；详情只读渲染 identifier（name）） */
  identifier?: string;
  /** 产品名称（对齐 ProductEditShowVo.name） */
  name?: string;
  /** 产品负责人（对齐 ProductEditShowVo.product_managers） */
  product_managers?: ProductManagerVo[];
  /** 产品发行区域（对齐 ProductEditShowVo.release_regions） */
  release_regions?: string[];
  /** 产品发行区域下拉选项（对齐 ProductEditShowVo.options.release_regions，Apollo 动态配置；含 USA/RUSSIA 等扩展值） */
  release_region_options?: NameValueVoString[];
  /** 联动所属区域（需求侧自填，无接口对应） */
  linked_release_region?: string[];
  /** 联动上架平台（需求侧自填，镜像 linked_release_region；选项取产品上架平台 launch_platforms） */
  linked_platform?: string[];
  /** 产品上架平台（对齐 ProductEditShowVo.launch_platforms） */
  launch_platforms?: string[];
  /** 联动上架平台（需求侧自填，<= 产品上架平台；20260812 新增，无接口对应） */
  linked_launch_platform?: string[];
  /** 平台数据互通（对齐 ProductEditShowVo.data_interconnection；0=不支持 1=支持） */
  data_interconnection?: number;
  /** 授权时长（二级单授权信息；多产品合并时按产品各自维护） */
  auth_duration?: string;
  /** 上线联动明细（二级单授权信息；多产品合并时按产品各自维护） */
  online_schedule?: SecondaryOnlineDetailItem[];
}

/**
 * 一级单详情 product_snapshot（ProductSnapshotVO 单对象）→ 前端 products 数组（IPCooperationProductItem[]）。
 * 一级单默认仅单一产品，故转换为单元素数组对齐 ProductInfoTable 表格结构。
 * @param snapshot 后端 l1_info.product_snapshot（identifier/name/product_managers/release_regions/launch_platforms/data_interconnection；
 * id 不在快照，由 id 参数（调用方传 l1_info.product_id）填充）
 * 联动所属区域（linked_release_region）不在 product_snapshot，由调用方 flattenL1DetailForForm 按 l1_info.linked_region 覆盖
 */
export function productSnapshotToProducts(
  id: number,
  snapshot?: any
): IPCooperationProductItem[] {
  if (!snapshot) return [];
  return [
    {
      // id 由调用方按 l1_info.product_id 传入；identifier/name/product_managers 直接取后端快照（ProductSnapshotVo 已含），无需额外拉取产品接口
      id: id,
      identifier: snapshot.identifier,
      name: snapshot.name,
      product_managers: snapshot.product_managers,
      release_regions: snapshot.release_regions,
      // 发行区域选项（Apollo 动态配置）用于渲染时翻译 USA/RUSSIA 等扩展值
      release_region_options: snapshot.release_region_options,
      // 联动所属区域占位：product_snapshot（ProductSnapshotVo）无此字段，实际值由 flattenL1DetailForForm 按 l1_info.linked_region 覆盖
      linked_release_region: [],
      // 联动上架平台占位：镜像 linked_release_region，实际值由 flattenL1DetailForForm 按 l1_info.linked_platform 覆盖
      linked_platform: [],
      launch_platforms: snapshot.launch_platforms,
      // data_interconnection(bool) → 0/1（对齐 ProductEditShowVo）
      data_interconnection: snapshot.data_interconnection ? 1 : 0,
    },
  ];
}

/**
 * 一级单上线联动明细：key 直接对齐接口 IpCooperationDemandDetailVoOnlineDetail
 * （online_begin/online_end/offline_begin/offline_end），无需 *_at 重命名映射。
 */
export interface IPCooperationOnlineDetailItem {
  online_begin?: string;
  online_end?: string;
  offline_begin?: string;
  offline_end?: string;
  /** 预计联动时长数值（必填，对齐接口 IpCooperationDemandCreateFormIpCooperationOnlineDetailForm.duration） */
  duration?: number;
  /** 预计联动时长单位（周/月，对齐接口 unit 契约 WEEK/MONTH） */
  unit?: 'WEEK' | 'MONTH';
}

// 列表列/筛选配置已提取至 ./ListConfig（对齐后端 IpCooperationWorkspaceListVo 契约 §3.7-3.9），此处仅保留表单占位；
// 状态枚举与中文映射已收敛，改用 api 生成 IpCooperationDemandStateEnum（enums.IpCooperationDemandStateEnum），前端不再自维护

export const defaultIPCooperationFormData = {
  // 新建一级单仅允许 1 个产品：初始化 1 个空产品行（隐藏「新增产品」按钮后仍可选取产品）
  products: [{}] as IPCooperationProductItem[],
  online_details: [{}] as IPCooperationOnlineDetailItem[],
  // 需求总预算默认币种：currencytype 初始值（formItemKeyMap 拆分 total_budget + currency），默认人民币
  $total_budget: { value: undefined, currency: DEFAULT_CURRENCY },
  // 币种默认人民币（对齐 DEFAULT_CURRENCY，喂 formItemKeyMap 隐藏 currency 子字段初始值）
  currency: DEFAULT_CURRENCY,
  // 评估报告评估人：新建态初始无值（复投IP + 游戏产品项目组自估时展示并必填）
  evaluation_users: [],
};

/**
 * 原币 → 人民币汇率查询（选择原币币种后自动带入汇率用，镜像 BudgetDetailTable/sort.ts 换算方向与 MPCProCurrency 匹配写法）。
 *  - 原币为空 → undefined
 *  - 原币为人民币（rmb/cny，大小写不敏感）→ 1（同币种）
 *  - 外币 → 查汇率表 from_currency=原币 → to_currency=人民币（大小写不敏感），命中返回汇率，未命中 console.warn + undefined（交回用户手动填写）
 */
export function getCnyExchangeRate(
  changeRateList: ExchangeRateDto[],
  currency?: string
): number | undefined {
  if (!currency) return undefined;
  if (isRmbCurrency(currency)) return SAME_CURRENCY_RATE;
  const from = currency.toLowerCase();
  const rate = changeRateList.find(
    (item) =>
      item.from_currency?.toLowerCase() === from &&
      item.to_currency?.toLowerCase() === DEFAULT_CURRENCY
  )?.exchange_rate;
  if (rate == null) {
    // eslint-disable-next-line no-console
    console.warn(
      `[IPCooperationDemand] 找不到 ${currency} → 人民币的汇率，请手动填写`
    );
  }
  return rate;
}

/**
 * 通用币种汇率查询（原币 → 目标币种，用于 Offer确认金额分配表「原币 → 谈判币种」换算）。
 * 镜像 getCnyExchangeRate 的大小写不敏感匹配 + console.warn 未命中范式：
 *  - 原币/目标币种任一为空 → undefined
 *  - 原币 == 目标币种（大小写不敏感）→ 1（同币种恒 1）
 *  - 外币 → 查汇率表 from_currency=原币 → to_currency=目标币种（大小写不敏感），命中返回汇率，未命中 console.warn + undefined（交回用户手动填写）
 */
export function getExchangeRate(
  changeRateList: ExchangeRateDto[],
  fromCurrency?: string,
  toCurrency?: string
): number | undefined {
  if (!fromCurrency || !toCurrency) return undefined;
  const from = fromCurrency.toLowerCase();
  const to = toCurrency.toLowerCase();
  if (from === to) return SAME_CURRENCY_RATE;
  const rate = changeRateList.find(
    (item) =>
      item.from_currency?.toLowerCase() === from &&
      item.to_currency?.toLowerCase() === to
  )?.exchange_rate;
  if (rate == null) {
    // eslint-disable-next-line no-console
    console.warn(
      `[IPCooperationDemand] 找不到 ${fromCurrency} → ${toCurrency} 的汇率，请手动填写`
    );
    return undefined;
  }
  // 保留 4 位小数（对齐汇率列 precision: 4；需求「自动带入保留4位小数」，decimal.js 避免浮点 toFixed 舍入边界）
  return new Decimal(rate).toDecimalPlaces(4).toNumber();
}
